import { api } from 'api/api';
import { Endpoints } from 'api/constants';
import { TableDataResponse } from 'types/general';
import {
  IApiIntegrationLogEvent,
  IIntegration,
  IIntegrationLog,
  IIntegrationLogItem,
  IIntegrationModel,
} from 'types/income';
import {
  ApiIntegrationLogsFilters,
  IntegrationLogItemsFilters,
  IntegrationLogsFilters,
  IntegrationStatus,
} from './integrations.types';

interface GetIntegration {
  integrationId: string;
}

interface CreateIntegration {
  integrationModelId: string;
  parentCompanyId: string;
  name: string;
  connectionData: any;
}

interface GetIntegrationModel {
  integrationModelId: string;
}

interface CreateIntegrationModel {
  name: string;
  comment?: string;
  logoId: string | null;
  type: string;
}

interface UpdateIntegrationModel {
  integrationModelId: string;
  data: {
    name: string;
    comment: string;
    logoId: string;
    type: string;
    status: IntegrationStatus;
  };
}

interface DeleteIntegrationModel {
  integrationModelId: string;
}

interface UpdateIntegration {
  integrationId: string;
  data: {
    name: string;
    status: IntegrationStatus;
    connectionData: any;
  };
}

interface DeleteIntegration {
  integrationId: string;
}

interface GetIntegrationLogs {
  integrationModelId: string;
  filters: Partial<IntegrationLogsFilters>;
}

interface GetIntegrationLogItems {
  processId: string;
  filters: Partial<IntegrationLogItemsFilters>;
}

interface GetApiIntegrationLogs {
  integrationId: string;
  filters: Partial<ApiIntegrationLogsFilters>;
  nextToken?: string;
}

export const loaders = {
  getIntegrations: () => {
    return api<TableDataResponse<IIntegration>>({
      url: Endpoints.clientAdmin.integrations.root,
    });
  },
  getIntegration: ({ integrationId }: GetIntegration) => {
    return api<IIntegration>({
      url: Endpoints.clientAdmin.integrations.byId.replace(
        ':integrationId',
        integrationId,
      ),
    });
  },
  createIntegration: ({
    integrationModelId,
    parentCompanyId,
    name,
    connectionData,
  }: CreateIntegration) => {
    return api<{ webhookToken?: string }>({
      url: Endpoints.clientAdmin.integrations.root,
      data: {
        integrationTypeId: integrationModelId,
        parentCompanyId,
        name,
        connectionData,
      },
      method: 'post',
    });
  },
  updateIntegration: ({ integrationId, data }: UpdateIntegration) => {
    return api({
      url: Endpoints.clientAdmin.integrations.byId.replace(
        ':integrationId',
        integrationId,
      ),
      data,
      method: 'put',
    });
  },
  deleteIntegration: ({ integrationId }: DeleteIntegration) => {
    return api({
      url: Endpoints.clientAdmin.integrations.byId.replace(
        ':integrationId',
        integrationId,
      ),
      method: 'delete',
    });
  },
  getIntegrationModels: () => {
    return api<TableDataResponse<IIntegrationModel>>({
      url: Endpoints.clientAdmin.integrations.models.root,
    });
  },
  getIntegrationModel: ({ integrationModelId }: GetIntegrationModel) => {
    return api<IIntegrationModel>({
      url: Endpoints.clientAdmin.integrations.models.byId.replace(
        ':integrationModelId',
        integrationModelId,
      ),
    });
  },
  createIntegrationModel: ({
    name,
    type,
    comment,
    logoId,
  }: CreateIntegrationModel) => {
    return api<{ id: string }>({
      url: Endpoints.clientAdmin.integrations.models.root,
      data: { name, type, comment, logoId },
      method: 'post',
    });
  },
  updateIntegrationModel: ({
    integrationModelId,
    data,
  }: UpdateIntegrationModel) => {
    return api<{ id: string }>({
      url: Endpoints.clientAdmin.integrations.models.byId.replace(
        ':integrationModelId',
        integrationModelId,
      ),
      data,
      method: 'put',
    });
  },
  deleteIntegrationModel: ({ integrationModelId }: DeleteIntegrationModel) => {
    return api<{ id: string }>({
      url: Endpoints.clientAdmin.integrations.models.byId.replace(
        ':integrationModelId',
        integrationModelId,
      ),
      method: 'delete',
    });
  },
  getIntegrationLogs: ({ integrationModelId, filters }: GetIntegrationLogs) => {
    return api<{ data: IIntegrationLog[] }>({
      url: Endpoints.clientAdmin.integrations.logs.replace(
        ':integrationModelId',
        integrationModelId,
      ),
      params: {
        filter: {
          status: filters.status,
        },
      },
    });
  },
  getIntegrationLogItems: ({ processId, filters }: GetIntegrationLogItems) => {
    return api<{ data: IIntegrationLogItem[] }>({
      url: Endpoints.clientAdmin.integrations.logItems.replace(
        ':processId',
        processId,
      ),
      params: {
        filter: {
          level: filters.level,
        },
      },
    });
  },
  getIntegrationLogEvents: ({
    integrationId,
    filters,
    nextToken,
  }: GetApiIntegrationLogs) => {
    return api<{
      events: IApiIntegrationLogEvent[];
      nextToken: string | null;
    }>({
      url: Endpoints.clientAdmin.integrations.apiLogs.replace(
        ':integrationId',
        integrationId,
      ),
      params: {
        level: filters.level,
        search: filters.search || undefined,
        from: filters.date?.from,
        to: filters.date?.to,
        limit: filters.limit,
        nextToken: nextToken || undefined,
      },
    });
  },
};
